/**
 * `disposerMath(noeud) → BoiteMath | null` — la MISE EN PAGE.
 *
 * ═══ Pourquoi la mise en page vit ici, dans `@silex/shared` ═══
 *
 * Exactement les raisons qui ont mis `resoudreFigure` ici, et une de plus.
 *
 *  1. **Deux rendus, une seule liste d'affichage.** Le SVG de l'app et
 *     l'image du courriel au parent consomment la MÊME `BoiteMath`. C'est la
 *     seule façon peu coûteuse de garantir qu'ils disent la même chose d'une
 *     même fraction (§4 de la spec de représentation visuelle).
 *  2. **Fonction PURE et sans dépendance.** Un énoncé se recompose à chaque
 *     bascule de thème, à chaque rotation, à chaque changement de taille de
 *     police système. `resoudreFigure` est un `useMemo` dans `<Figure>` pour
 *     cette raison ; mettre la mise en page d'une fraction derrière un appel
 *     réseau reviendrait à faire dépendre l'affichage d'un énoncé de la
 *     connectivité — sur un produit qui doit tenir en classe, c'est non.
 *  3. **Le déterminisme rend le cache inutile.** La sortie est fonction de la
 *     seule entrée : rien à mémoriser, donc aucun état indéterminé possible.
 *     C'est la version la plus forte de la discipline du cache de verdicts —
 *     ne pas avoir de cache du tout.
 *
 * Une différence assumée avec le solveur de figures : `resoudreFigure` REFUSE
 * (`null`) une figure géométriquement impossible, parce qu'une figure fausse
 * enseigne une erreur. Une expression, elle, n'a pas d'impossibilité
 * géométrique : `disposerMath` ne rend `null` que si l'arbre dépasse les
 * bornes de profondeur ou de taille, et l'appelant retombe alors sur le
 * TEXTE — un rendu dégradé vaut mieux qu'un écran vide.
 *
 * ═══ L'unité ═══
 *
 * Tout est exprimé en **cadratins** (multiples de la taille de police de
 * base). Le rendu multiplie par une seule taille en pixels. C'est ce qui rend
 * le rendu identique quel que soit l'appareil (invariant produit 8) : rien
 * n'est arrondi à un pixel ici, et le SVG reste net à tout zoom.
 *
 * ═══ La métrique ═══
 *
 * Les atomes sont composés en **IBM Plex Mono** — la police « données » du
 * brand book, la seule chargée en chasse fixe. Ce n'est pas un pis-aller :
 * une chasse FIXE rend la largeur d'un atome exactement calculable sans table
 * de métriques, donc calculable dans une fonction pure partagée. C'est la
 * même contrainte que `resoudreFigure` a déjà résolue de la même façon pour
 * les étiquettes de figure.
 */
import { sansCible } from './expression.js';
import { metriqueMath, trancherGlyphe } from './metrique.js';
/* ───────────────────────────────────────────────────────────── métrique */
/**
 * Chasse d'IBM Plex Mono : 600/1000 unités de cadratin. Exacte, pas moyenne.
 *
 * C'est la métrique PAR DÉFAUT (`METRIQUE_MONO`). Un rendu qui dessine dans une
 * police proportionnelle choisit la sienne (`choisirMetriqueMath`) : avances,
 * hauteurs et élévation de l'exposant se lisent alors dans `metrique.ts`.
 */
export const AVANCE_MONO = 0.6;
/** Montante, œil et descendante de la police du TEXTE (`\text{…}`), en cadratins. */
const MONTEE_TEXTE = 0.74;
const OEIL_TEXTE = 0.55;
const DESCENTE_TEXTE = 0.24;
/** Hauteur de l'AXE mathématique au-dessus de la ligne de base : la barre de fraction s'y pose. */
const AXE = 0.28;
/** Épaisseur d'un filet (barre de fraction, barre de radical, barre de valeur absolue). */
const EPAISSEUR_FILET = 0.055;
/** Dégagement au-dessus et au-dessous de la barre de fraction. */
const AIR_NUMERATEUR = 0.13;
const AIR_DENOMINATEUR = 0.13;
/** Débord horizontal de la barre de fraction de chaque côté. */
const DEBORD_BARRE = 0.09;
/** Espacement de part et d'autre d'un opérateur binaire, puis d'une relation. */
const AIR_OPERATEUR = 0.16;
const AIR_RELATION = 0.22;
/** Ce qui sépare un signe de ce qu'il signe : presque rien. */
const AIR_SIGNE = 0.03;
const SIGNES_UNAIRES = '−+±';
const estEspace = (n) => n !== undefined && n.type === 'atome' && n.texte.length > 0 && /^\u00a0+$/.test(n.texte);
/**
 * Les relations : elles respirent plus qu'un opérateur binaire. Les FLÈCHES en
 * sont — `1 → a`, `y = 2x + 1 ⇒ m = 2` : elles relient deux membres, elles ne
 * les combinent pas, et serrées comme un `+` elles se liraient comme un signe.
 */
const RELATIONS = '=<>≤≥≠≈≡→⇒⇔↦∥⊥∈∉∪∩';
/** Facteur de taille des scripts (exposants, indices, limites), par niveau d'imbrication. */
const TAILLES_SCRIPT = [1, 0.72, 0.58, 0.5];
/** Abaissement d'un indice, en cadratins de la base. (L'élévation de l'exposant : la métrique.) */
const ABAISSEMENT_INDICE = 0.18;
/** Largeur d'un délimiteur étirable et d'un signe radical. */
const LARGEUR_DELIMITEUR = 0.3;
const LARGEUR_RADICAL = 0.62;
/** Profondeur maximale de l'arbre. Au-delà, on retombe sur le texte plutôt que sur du confetti. */
const PROFONDEUR_MAX = 8;
/** Nombre maximal de dessins produits. Garde-fou de coût sur un appareil d'entrée de gamme. */
const DESSINS_MAX = 400;
const vide = (largeur) => ({
    largeur,
    montee: 0,
    descente: 0,
    dessins: [],
});
function decaler(boite, dx, dy) {
    if (dx === 0 && dy === 0)
        return boite.dessins;
    return boite.dessins.map((d) => {
        switch (d.type) {
            case 'glyphe':
                return { ...d, x: d.x + dx, y: d.y + dy };
            case 'filet':
                return { ...d, x: d.x + dx, y: d.y + dy };
            case 'barre':
                return { ...d, x: d.x + dx, yHaut: d.yHaut + dy, yBas: d.yBas + dy };
            case 'chemin':
                return { ...d, d: decalerChemin(d.d, dx, dy) };
            case 'repere':
                return { ...d, x: d.x + dx, y: d.y + dy };
        }
    });
}
/** La même boîte, décalée. Une translation verticale déplace la ligne de base. */
function translater(b, dx, dy) {
    return {
        largeur: b.largeur,
        montee: b.montee - dy,
        descente: b.descente + dy,
        dessins: decaler(b, dx, dy),
    };
}
/**
 * Décale un chemin. Les chemins sont produits ICI, avec un vocabulaire réduit
 * (`M`, `L`, `C`, `Q`) et des coordonnées absolues par paires : le décalage
 * est donc une réécriture sûre, et non une analyse de SVG quelconque.
 */
function decalerChemin(d, dx, dy) {
    const morceaux = d.split(' ');
    let indiceCoordonnee = 0;
    return morceaux
        .map((m) => {
        if (/^[MLCQ]$/.test(m)) {
            indiceCoordonnee = 0;
            return m;
        }
        const v = Number(m);
        if (Number.isNaN(v))
            return m;
        const decale = indiceCoordonnee % 2 === 0 ? v + dx : v + dy;
        indiceCoordonnee += 1;
        return arrondi(decale);
    })
        .join(' ');
}
const arrondi = (v) => (Math.round(v * 10000) / 10000).toString();
const pt = (x, y) => `${arrondi(x)} ${arrondi(y)}`;
const tailleDe = (niveau) => (TAILLES_SCRIPT[Math.min(niveau, TAILLES_SCRIPT.length - 1)] ?? 0.5) * metriqueMath().echelle;
/** Le nombre de POINTS DE CODE — `[...t].length`, pas `t.length` : `²` compte pour un. */
const longueurVisuelle = (t) => [...t].length;
/** Les caractères qui descendent SOUS la ligne de base. */
const DESCENDEURS = 'gjpqyµ,;()[]{}|/√∑∏∫';
/** Les caractères qui montent jusqu'à la hauteur d'ascendante ou de capitale. */
const ASCENDEURS = '0123456789bdfhklt(){}[]|/√∑∏∫ABCDEFGHIJKLMNOPQRSTUVWXYZ²³¹⁰⁴⁵⁶⁷⁸⁹ⁿΔΘ';
/** Ce qui reste sous la ligne de base quand aucun caractère ne descend. */
const DESCENTE_PLATE = 0.02;
/**
 * La boîte RÉELLE d'un texte, et non sa boîte de police.
 *
 * Sans cette mesure, `2` sur `4` réserverait la descendante d'un `g` de part
 * et d'autre de la barre : une fraction de chiffres serait un tiers trop
 * haute, et elle repousserait la ligne de texte qui la porte. Une chasse
 * fixe rend la largeur exacte ; le jeu de caractères rend la hauteur exacte.
 */
function mesurerTexte(texte, hauteurs) {
    let monte = false;
    let descend = false;
    for (const c of texte) {
        if (ASCENDEURS.includes(c))
            monte = true;
        if (DESCENDEURS.includes(c))
            descend = true;
    }
    return {
        montee: monte ? hauteurs.montee : hauteurs.oeil,
        descente: descend ? hauteurs.descente : DESCENTE_PLATE,
    };
}
const HAUTEURS_TEXTE = { montee: MONTEE_TEXTE, oeil: OEIL_TEXTE, descente: DESCENTE_TEXTE };
function glyphe(texte, taille, role) {
    const m = metriqueMath();
    if (m.polices === null) {
        const largeur = longueurVisuelle(texte) * AVANCE_MONO * taille;
        const { montee, descente } = mesurerTexte(texte, m);
        return {
            largeur,
            montee: montee * taille,
            descente: descente * taille,
            dessins: [{ type: 'glyphe', texte, x: largeur / 2, y: 0, taille, role }],
        };
    }
    // Police proportionnelle : une tranche par police (chiffres droits, variable
    // en italique, mot dans la police du texte), chacune à sa largeur LUE.
    const dessins = [];
    let x = 0;
    let montee = 0;
    let descente = 0;
    for (const tranche of trancherGlyphe(texte, role, m)) {
        // Un mot garde le corps du texte voisin : l'échelle des mathématiques ne
        // le grossit pas.
        const corps = tranche.police === 'texte' ? taille / m.echelle : taille;
        const largeur = tranche.largeur * corps;
        const h = mesurerTexte(tranche.texte, tranche.police === 'texte' ? HAUTEURS_TEXTE : m);
        dessins.push({
            type: 'glyphe',
            texte: tranche.texte,
            x: x + largeur / 2,
            y: 0,
            taille: corps,
            role,
            ...(tranche.police === undefined ? {} : { police: tranche.police }),
        });
        x += largeur;
        montee = Math.max(montee, h.montee * corps);
        descente = Math.max(descente, h.descente * corps);
    }
    return { largeur: x, montee, descente, dessins };
}
/** Aligne des boîtes sur une ligne de base commune. */
function enRangee(boites) {
    let largeur = 0;
    let montee = 0;
    let descente = 0;
    const dessins = [];
    for (const b of boites) {
        dessins.push(...decaler(b, largeur, 0));
        largeur += b.largeur;
        montee = Math.max(montee, b.montee);
        descente = Math.max(descente, b.descente);
    }
    return { largeur, montee, descente, dessins };
}
/**
 * L'air À GAUCHE et À DROITE d'un opérateur, en cadratins de `taille`.
 *
 * `,` `;` `:` sont classés OPÉRATEURS par l'analyseur, et volontairement :
 * `decrireMath` dirait sinon « a fois virgule fois b » à un lecteur d'écran
 * (voir `OPERATEURS_CARACTERES`, `latex.ts`). Mais ce sont des signes de
 * PONCTUATION, pas des opérations, et leur donner l'air d'un `+` des deux
 * côtés écrivait « 4 , 9 , 14 , 19 » — une espace devant chaque virgule.
 *
 * La règle appliquée est celle du français, exactement celle que suit déjà la
 * phrase annoncée au lecteur d'écran (`direTexteMath`, `decrire.ts`) :
 *
 *  - la VIRGULE se colle à ce qui la précède : rien à gauche, l'air habituel
 *    à droite ;
 *  - `;` et `:` sont une ponctuation DOUBLE, devant laquelle le français met
 *    une espace : ils gardent leur air des deux côtés. Cela laisse juste, du
 *    même coup, le `:` d'un RAPPORT (`3 : 4`), qui s'écrit espacé lui aussi.
 *
 * La virgule DÉCIMALE ne passe jamais par ici : elle est lue à l'intérieur de
 * son nombre (`3,14` est un atome). Et rien de tout cela ne touche la
 * description au lecteur d'écran, qui ne lit pas la mise en page.
 */
function margesOperateur(texte, taille) {
    if (texte === ',')
        return [0, AIR_OPERATEUR * taille];
    // En chasse proportionnelle, la barre oblique d'un exposant fractionnaire
    // (`16^{1/2}`) se serre contre ses chiffres : espacée, elle se lit comme une
    // division entre deux nombres.
    if (texte === '/' && metriqueMath().polices !== null)
        return [0.02 * taille, 0.02 * taille];
    const air = (RELATIONS.includes(texte) ? AIR_RELATION : AIR_OPERATEUR) * taille;
    return [air, air];
}
function disposerNoeud(noeud, ctx) {
    if (ctx.profondeur > PROFONDEUR_MAX)
        return null;
    const taille = tailleDe(ctx.niveau);
    const dedans = { ...ctx, profondeur: ctx.profondeur + 1 };
    const script = {
        niveau: ctx.niveau + 1,
        profondeur: ctx.profondeur + 1,
        ...(ctx.dansFraction === true ? { dansFraction: true } : {}),
    };
    switch (noeud.type) {
        case 'atome':
            return glyphe(noeud.texte, taille, 'operande');
        case 'operateur': {
            const corps = glyphe(noeud.texte, taille, 'operateur');
            const [gauche, droite] = margesOperateur(noeud.texte, taille);
            return {
                largeur: corps.largeur + gauche + droite,
                montee: corps.montee,
                descente: corps.descente,
                dessins: decaler(corps, gauche, 0),
            };
        }
        case 'rangee': {
            const boites = [];
            noeud.enfants.forEach((enfant, i) => {
                if (boites.length < i)
                    return;
                // Un signe en place d'OPÉRANDE (en tête, ou après un opérateur) signe
                // ce qui le suit : il ne prend pas l'air d'une soustraction. Devant
                // un nombre nu, l'analyseur l'a déjà fusionné (`−5`) ; il restait le
                // signe posé devant une puissance ou une fraction — `−3²` s'écrivait
                // « − 3² », l'écriture même que la fiche sur le signe fait lire.
                if (enfant.type === 'operateur' && SIGNES_UNAIRES.includes(enfant.texte)) {
                    let k = i - 1;
                    while (k >= 0 && estEspace(noeud.enfants[k]))
                        k -= 1;
                    const precedent = noeud.enfants[k];
                    if (precedent === undefined || sansCible(precedent).type === 'operateur') {
                        const corps = glyphe(enfant.texte, taille, 'operateur');
                        boites.push({ ...corps, largeur: corps.largeur + AIR_SIGNE * taille });
                        return;
                    }
                }
                const b = disposerNoeud(enfant, dedans);
                if (b !== null)
                    boites.push(b);
            });
            if (boites.length < noeud.enfants.length)
                return null;
            return enRangee(boites);
        }
        case 'fraction':
            return disposerFraction(noeud, ctx);
        case 'script':
            return disposerScript(noeud, ctx, dedans, script);
        case 'racine':
            return disposerRacine(noeud, ctx, dedans);
        case 'valeurAbsolue':
        case 'norme': {
            const contenu = disposerNoeud(noeud.contenu, dedans);
            if (contenu === null)
                return null;
            const air = 0.1 * taille;
            const ep = EPAISSEUR_FILET * taille;
            // La norme (tranche T43) : DEUX barres fines de chaque côté, séparées
            // d'un écart qui les distingue d'un trait épais à toute taille.
            const ecart = noeud.type === 'norme' ? 0.14 * taille : 0;
            const marge = 0.12 * taille + ecart;
            const haut = -(contenu.montee + air);
            const bas = contenu.descente + air;
            const largeur = contenu.largeur + 2 * marge;
            const barre = (x) => ({
                type: 'barre',
                x,
                yHaut: haut,
                yBas: bas,
                epaisseur: ep,
            });
            const gauche = [barre(ep / 2), ...(ecart > 0 ? [barre(ep / 2 + ecart)] : [])];
            const droite = [
                ...(ecart > 0 ? [barre(largeur - ep / 2 - ecart)] : []),
                barre(largeur - ep / 2),
            ];
            return {
                largeur,
                montee: contenu.montee + air,
                descente: contenu.descente + air,
                dessins: [...gauche, ...decaler(contenu, marge, 0), ...droite],
            };
        }
        case 'surmonte':
            return disposerSurmonte(noeud, ctx, dedans);
        case 'groupe':
            return disposerGroupe(noeud, ctx, dedans);
        case 'systeme':
            return disposerSysteme(noeud, ctx, dedans);
        case 'grandOperateur':
            return disposerGrandOperateur(noeud, ctx, dedans, script);
        case 'cible': {
            // Le contenu est composé DANS LE CONTEXTE de la cible, sans cran de
            // profondeur ni de taille : marquer un morceau ne le déplace pas.
            const contenu = disposerNoeud(noeud.contenu, ctx);
            if (contenu === null)
                return null;
            return {
                ...contenu,
                dessins: [
                    ...contenu.dessins,
                    {
                        type: 'repere',
                        nom: noeud.nom,
                        x: 0,
                        y: -contenu.montee,
                        largeur: contenu.largeur,
                        hauteur: contenu.montee + contenu.descente,
                    },
                ],
            };
        }
        case 'sousAccolade':
            return disposerSousAccolade(noeud, dedans, script, taille);
        case 'matrice':
            return disposerMatrice(noeud, ctx, dedans);
    }
}
/**
 * Une MATRICE (tranche T41). Chaque colonne prend la largeur de sa plus large
 * case, et chaque case y est CENTRÉE — la règle de `pmatrix`. Les lignes sont
 * posées à pas régulier : une case qui porte une fraction écarte sa ligne, mais
 * une ligne de chiffres garde la hauteur d'une ligne de chiffres (le « strut »
 * de LaTeX), sans quoi `0` et `-1` ne tomberaient pas sur la même ligne de base
 * que leurs voisins. Le bloc est centré sur l'AXE, comme une fraction et un
 * système, et les délimiteurs s'étirent sur toute sa hauteur.
 */
function disposerMatrice(noeud, ctx, dedans) {
    const taille = tailleDe(ctx.niveau);
    const cases = [];
    for (const ligne of noeud.lignes) {
        const boites = [];
        for (const c of ligne) {
            const b = disposerNoeud(c, dedans);
            if (b === null)
                return null;
            boites.push(b);
        }
        cases.push(boites);
    }
    const colonnes = Math.max(0, ...cases.map((l) => l.length));
    if (colonnes === 0)
        return null;
    const largeurs = [];
    for (let k = 0; k < colonnes; k += 1) {
        largeurs.push(Math.max(0, ...cases.map((l) => l[k]?.largeur ?? 0)));
    }
    /* L'écart entre deux colonnes : un cadratin, celui de `\arraycolsep` des
       deux côtés. Moins, `-1  0` se lirait « −10 ». */
    const ecart = 0.9 * taille;
    const montant = 0.72 * taille;
    const descendant = 0.26 * taille;
    const interligne = 0.3 * taille;
    const empile = [];
    let y = 0;
    cases.forEach((boites, i) => {
        const haut = Math.max(montant, ...boites.map((b) => b.montee));
        const bas = Math.max(descendant, ...boites.map((b) => b.descente));
        y += haut;
        let x = 0;
        boites.forEach((b, k) => {
            const l = largeurs[k] ?? 0;
            empile.push(...decaler(b, x + (l - b.largeur) / 2, y));
            x += l + ecart;
        });
        y += bas + (i < cases.length - 1 ? interligne : 0);
    });
    const hauteur = y;
    const largeurCorps = largeurs.reduce((s, l) => s + l, 0) + ecart * (colonnes - 1);
    const centre = hauteur / 2 + AXE * taille;
    const air = 0.08 * taille;
    // Une parenthèse plus haute est plus large : sans cela, sur deux lignes, elle
    // devient un trait presque droit.
    const w = LARGEUR_DELIMITEUR * taille * Math.min(1.6, 1 + (hauteur / taille - 1) * 0.25);
    const ep = EPAISSEUR_FILET * taille;
    const montee = centre + air;
    const descente = hauteur - centre + air;
    const corps = { largeur: largeurCorps, montee: 0, descente: hauteur, dessins: empile };
    // De part et d'autre, plus d'air qu'en hauteur : une case collée à la
    // parenthèse (« (0 ») se lirait comme un groupe ordinaire.
    const airCote = 0.16 * taille;
    const place = translater(corps, w + airCote, -centre);
    const largeur = largeurCorps + 2 * (w + airCote);
    return {
        largeur,
        montee,
        descente,
        dessins: [
            {
                type: 'chemin',
                d: cheminDelimiteur(noeud.delimiteur, true, 0, -montee, descente, w),
                epaisseur: ep,
            },
            ...place.dessins,
            {
                type: 'chemin',
                d: cheminDelimiteur(noeud.delimiteur, false, largeur - w, -montee, descente, w),
                epaisseur: ep,
            },
        ],
    };
}
/**
 * Un contenu, l'accolade tracée dessous à sa largeur, la légende centrée sous
 * la pointe. Même principe que `surmonte` : on mesure, puis on trace — rien ne
 * dépend d'un glyphe d'accolade que la police n'étirerait pas.
 */
function disposerSousAccolade(noeud, dedans, script, taille) {
    const c = disposerNoeud(noeud.contenu, dedans);
    const l = disposerNoeud(noeud.legende, script);
    if (c === null || l === null)
        return null;
    const ep = EPAISSEUR_FILET * taille;
    const air = 0.1 * taille;
    const h = 0.11 * taille;
    const largeur = Math.max(c.largeur, l.largeur);
    const x1 = (largeur - c.largeur) / 2;
    const x2 = x1 + c.largeur;
    const m = (x1 + x2) / 2;
    const r = Math.min(0.16 * taille, c.largeur / 4);
    const y0 = c.descente + air;
    const accolade = [
        'M',
        pt(x1, y0),
        'Q',
        pt(x1, y0 + h),
        pt(x1 + r, y0 + h),
        'L',
        pt(m - r, y0 + h),
        'Q',
        pt(m, y0 + h),
        pt(m, y0 + 2 * h),
        'Q',
        pt(m, y0 + h),
        pt(m + r, y0 + h),
        'L',
        pt(x2 - r, y0 + h),
        'Q',
        pt(x2, y0 + h),
        pt(x2, y0),
    ].join(' ');
    const yLegende = y0 + 2 * h + air + l.montee;
    return {
        largeur,
        montee: c.montee,
        descente: yLegende + l.descente,
        dessins: [
            ...decaler(c, x1, 0),
            { type: 'chemin', d: accolade, epaisseur: ep },
            ...decaler(l, (largeur - l.largeur) / 2, yLegende),
        ],
    };
}
function disposerFraction(noeud, ctx) {
    const taille = tailleDe(ctx.niveau);
    // Une fraction DANS une fraction descend d'un cran de taille : c'est ce qui
    // rend `(1/2)/(3/4)` lisible au lieu de le rendre haut. La condition porte
    // sur le fait d'être DANS une fraction, jamais sur la profondeur de
    // l'arbre — sinon toute fraction posée dans une rangée (`3/5 = ?/20`)
    // rétrécirait sans raison, et `2/4` seule serait plus grosse que `2/4 + 1/3`.
    const interne = {
        niveau: ctx.niveau + (ctx.dansFraction === true ? 1 : 0),
        profondeur: ctx.profondeur + 1,
        dansFraction: true,
    };
    const num = disposerNoeud(noeud.numerateur, interne);
    const den = disposerNoeud(noeud.denominateur, interne);
    if (num === null || den === null)
        return null;
    const ep = EPAISSEUR_FILET * taille;
    const axe = AXE * taille;
    const debord = DEBORD_BARRE * taille;
    const largeurBarre = Math.max(num.largeur, den.largeur) + 2 * debord;
    // Une case de chasse fixe porte ses propres approches ; un glyphe
    // proportionnel, lui, viendrait toucher la barre (`\frac{3}{4}x`).
    const air = metriqueMath().airFraction * taille;
    const largeur = largeurBarre + 2 * air;
    const yNum = -(axe + ep / 2 + AIR_NUMERATEUR * taille + num.descente);
    const yDen = -axe + ep / 2 + AIR_DENOMINATEUR * taille + den.montee;
    return {
        largeur,
        montee: -yNum + num.montee,
        descente: yDen + den.descente,
        dessins: [
            ...decaler(num, (largeur - num.largeur) / 2, yNum),
            {
                type: 'filet',
                x: air,
                y: -axe,
                largeur: largeurBarre,
                epaisseur: ep,
            },
            ...decaler(den, (largeur - den.largeur) / 2, yDen),
        ],
    };
}
function disposerScript(noeud, ctx, dedans, script) {
    const taille = tailleDe(ctx.niveau);
    const base = disposerNoeud(noeud.base, dedans);
    if (base === null)
        return null;
    const exp = noeud.exposant === undefined ? null : disposerNoeud(noeud.exposant, script);
    const ind = noeud.indice === undefined ? null : disposerNoeud(noeud.indice, script);
    if ((noeud.exposant !== undefined && exp === null) ||
        (noeud.indice !== undefined && ind === null)) {
        return null;
    }
    const m = metriqueMath();
    // Une base PENCHÉE déborde à droite de sa chasse : l'exposant s'en écarte
    // d'autant, sans quoi le haut d'un `x` italique le touche.
    const dernier = base.dessins[base.dessins.length - 1];
    const penchee = dernier?.type === 'glyphe' && dernier.police === 'italique';
    const dx = base.largeur + (m.airScript + (penchee ? m.correctionItalique : 0)) * taille;
    const dessins = [...base.dessins];
    let montee = base.montee;
    let descente = base.descente;
    let largeurScripts = 0;
    if (exp !== null) {
        const y = -(m.elevationExposant * taille + Math.max(0, base.montee - m.montee * taille));
        dessins.push(...decaler(exp, dx, y));
        montee = Math.max(montee, -y + exp.montee);
        largeurScripts = Math.max(largeurScripts, exp.largeur);
    }
    if (ind !== null) {
        const y = ABAISSEMENT_INDICE * taille + base.descente;
        dessins.push(...decaler(ind, dx, y));
        descente = Math.max(descente, y + ind.descente);
        largeurScripts = Math.max(largeurScripts, ind.largeur);
    }
    return { largeur: dx + largeurScripts, montee, descente, dessins };
}
/**
 * Un trait tracé AU-DESSUS du contenu, à la largeur mesurée.
 *
 * Même principe que le radical : on mesure l'enfant, puis on trace par-dessus.
 * Rien ne dépend de la police, donc `\overline{AB}` porte UNE barre continue
 * et `\vec{AB}` UNE flèche — ce qu'aucun caractère combinant ne sait faire.
 */
function disposerSurmonte(noeud, ctx, dedans) {
    const taille = tailleDe(ctx.niveau);
    const c = disposerNoeud(noeud.contenu, dedans);
    if (c === null)
        return null;
    const ep = EPAISSEUR_FILET * taille;
    const air = 0.1 * taille;
    const y = -(c.montee + air + ep / 2);
    const filet = { type: 'filet', x: 0, y, largeur: c.largeur, epaisseur: ep };
    if (noeud.ornement === 'barre') {
        return {
            largeur: c.largeur,
            montee: c.montee + air + ep,
            descente: c.descente,
            dessins: [...c.dessins, filet],
        };
    }
    /* La pointe se dessine SUR la droite du filet, vers l'extérieur : une flèche
       dont la pointe déborderait changerait la largeur du groupe et décalerait
       tout ce qui suit. */
    const longueur = 0.24 * taille;
    const ouverture = 0.15 * taille;
    const pointe = [
        'M',
        pt(c.largeur - longueur, y - ouverture),
        'L',
        pt(c.largeur, y),
        'L',
        pt(c.largeur - longueur, y + ouverture),
    ].join(' ');
    return {
        largeur: c.largeur,
        montee: c.montee + air + ep + ouverture,
        descente: c.descente,
        dessins: [...c.dessins, filet, { type: 'chemin', d: pointe, epaisseur: ep }],
    };
}
function disposerRacine(noeud, ctx, dedans) {
    const taille = tailleDe(ctx.niveau);
    const rad = disposerNoeud(noeud.radicande, dedans);
    if (rad === null)
        return null;
    const ep = EPAISSEUR_FILET * taille;
    const air = 0.12 * taille;
    const larg = LARGEUR_RADICAL * taille;
    const montee = rad.montee + air + 2 * ep;
    const yHaut = -montee + ep / 2;
    const yBas = rad.descente;
    // Le signe radical : une jambe descendante, le crochet, puis la barre qui
    // couvre le radicande. Un seul tracé, épaisseur uniforme.
    const hauteur = yBas - yHaut;
    const chemin = [
        'M',
        pt(0, yBas - hauteur * 0.42),
        'L',
        pt(larg * 0.26, yBas - hauteur * 0.3),
        'L',
        pt(larg * 0.52, yBas),
        'L',
        pt(larg * 0.9, yHaut),
        'L',
        pt(larg + rad.largeur + air, yHaut),
    ].join(' ');
    const radical = {
        largeur: larg + rad.largeur + air,
        montee,
        descente: rad.descente,
        dessins: [{ type: 'chemin', d: chemin, epaisseur: ep }, ...decaler(rad, larg, 0)],
    };
    if (noeud.degre === undefined)
        return radical;
    // Racine n-ième : le degré se pose dans le creux du radical, deux crans plus
    // petit, et pousse le radical vers la droite s'il déborde.
    const degre = disposerNoeud(noeud.degre, {
        niveau: ctx.niveau + 2,
        profondeur: ctx.profondeur + 1,
        ...(ctx.dansFraction === true ? { dansFraction: true } : {}),
    });
    if (degre === null)
        return null;
    const yDegre = yHaut + hauteur * 0.28;
    const dx = Math.max(0, degre.largeur - larg * 0.45);
    const gauche = translater(degre, 0, yDegre);
    const droite = translater(radical, dx, 0);
    return {
        largeur: radical.largeur + dx,
        montee: Math.max(radical.montee, gauche.montee),
        descente: Math.max(radical.descente, gauche.descente),
        dessins: [...gauche.dessins, ...droite.dessins],
    };
}
/** Les tracés des délimiteurs étirables. Ils suivent la hauteur RÉELLE du contenu. */
function cheminDelimiteur(sorte, gauche, x, yHaut, yBas, w) {
    const int = gauche ? x + w : x; // bord intérieur
    const ext = gauche ? x : x + w; // bord extérieur
    const ym = (yHaut + yBas) / 2;
    if (sorte === 'parentheses') {
        const ctrl = gauche ? ext - w * 0.15 : ext + w * 0.15;
        return ['M', pt(int, yHaut), 'Q', pt(ctrl, ym), pt(int, yBas)].join(' ');
    }
    if (sorte === 'crochets') {
        return ['M', pt(int, yHaut), 'L', pt(ext, yHaut), 'L', pt(ext, yBas), 'L', pt(int, yBas)].join(' ');
    }
    // Accolade : deux boucles symétriques qui se rejoignent en pointe sur l'axe.
    const q = (yBas - yHaut) * 0.25;
    const mi = gauche ? x + w * 0.55 : x + w * 0.45;
    return [
        'M',
        pt(int, yHaut),
        'Q',
        pt(mi, yHaut + q * 0.2),
        pt(mi, yHaut + q),
        'L',
        pt(mi, ym - q * 0.25),
        'Q',
        pt(mi, ym),
        pt(ext, ym),
        'Q',
        pt(mi, ym),
        pt(mi, ym + q * 0.25),
        'L',
        pt(mi, yBas - q),
        'Q',
        pt(mi, yBas - q * 0.2),
        pt(int, yBas),
    ].join(' ');
}
function disposerGroupe(noeud, ctx, dedans) {
    const taille = tailleDe(ctx.niveau);
    const contenu = disposerNoeud(noeud.contenu, dedans);
    if (contenu === null)
        return null;
    const air = 0.08 * taille;
    const w = LARGEUR_DELIMITEUR * taille;
    const ep = EPAISSEUR_FILET * taille;
    const montee = contenu.montee + air;
    const descente = contenu.descente + air;
    const largeur = contenu.largeur + 2 * (w + air);
    return {
        largeur,
        montee,
        descente,
        dessins: [
            {
                type: 'chemin',
                d: cheminDelimiteur(noeud.delimiteur, true, 0, -montee, descente, w),
                epaisseur: ep,
            },
            ...decaler(contenu, w + air, 0),
            {
                type: 'chemin',
                d: cheminDelimiteur(noeud.delimiteur, false, largeur - w, -montee, descente, w),
                epaisseur: ep,
            },
        ],
    };
}
/**
 * Une rangée, coupée DEVANT chacune de ses relations de premier niveau :
 * `a = b = c` rend `a`, `= b`, `= c`. C'est là — et là seulement — qu'une
 * formule posée dans une phrase a le droit de passer à la ligne, comme dans
 * un manuel. Chaque morceau garde l'air de sa relation.
 */
export function couperAuxRelations(noeud) {
    if (noeud.type !== 'rangee')
        return [noeud];
    const morceaux = [[]];
    noeud.enfants.forEach((enfant, i) => {
        if (i > 0 && enfant.type === 'operateur' && RELATIONS.includes(enfant.texte))
            morceaux.push([]);
        morceaux[morceaux.length - 1]?.push(enfant);
    });
    if (morceaux.length === 1)
        return [noeud];
    return morceaux
        .filter((enfants) => enfants.length > 0)
        .map((enfants) => ({ type: 'rangee', enfants }));
}
/** L'indice, dans une rangée, de la relation de premier niveau — pour l'alignement d'un système. */
function indiceRelation(noeud) {
    if (noeud.type !== 'rangee')
        return -1;
    return noeud.enfants.findIndex((e) => e.type === 'operateur' && RELATIONS.includes(e.texte));
}
function disposerSysteme(noeud, ctx, dedans) {
    const taille = tailleDe(ctx.niveau);
    // Chaque ligne est coupée en deux à sa relation, quand elle en a une : les
    // `=` d'un système s'alignent, c'est ainsi qu'un système se LIT.
    const paires = [];
    for (const ligne of noeud.lignes) {
        const k = indiceRelation(ligne);
        if (k < 0 || ligne.type !== 'rangee') {
            const b = disposerNoeud(ligne, dedans);
            if (b === null)
                return null;
            paires.push({ gauche: b, droite: null });
            continue;
        }
        const g = disposerNoeud({ type: 'rangee', enfants: ligne.enfants.slice(0, k) }, dedans);
        const d = disposerNoeud({ type: 'rangee', enfants: ligne.enfants.slice(k) }, dedans);
        if (g === null || d === null)
            return null;
        paires.push({ gauche: g, droite: d });
    }
    const largeurGauche = Math.max(...paires.map((p) => p.gauche.largeur));
    const largeurDroite = Math.max(...paires.map((p) => p.droite?.largeur ?? 0));
    const interligne = 0.42 * taille;
    // Empilement dans un repère local dont l'origine est le HAUT du système.
    const empile = [];
    let y = 0;
    for (const p of paires) {
        const hautLigne = Math.max(p.gauche.montee, p.droite?.montee ?? 0);
        const basLigne = Math.max(p.gauche.descente, p.droite?.descente ?? 0);
        y += hautLigne;
        empile.push(...decaler(p.gauche, largeurGauche - p.gauche.largeur, y));
        if (p.droite !== null)
            empile.push(...decaler(p.droite, largeurGauche, y));
        y += basLigne + interligne;
    }
    const hauteurTotale = y - interligne;
    // Le système est centré verticalement sur l'AXE, comme une fraction : c'est
    // ce qui l'aligne avec le texte qui l'entoure au lieu de le faire flotter.
    const centre = hauteurTotale / 2 - AXE * taille;
    const w = LARGEUR_DELIMITEUR * taille * 1.4;
    const air = 0.14 * taille;
    const corps = {
        largeur: largeurGauche + largeurDroite,
        montee: 0,
        descente: hauteurTotale,
        dessins: empile,
    };
    const place = translater(corps, w + air, -centre);
    return {
        largeur: w + air + largeurGauche + largeurDroite,
        montee: centre,
        descente: hauteurTotale - centre,
        dessins: [
            {
                type: 'chemin',
                d: cheminDelimiteur('accolades', true, 0, -centre, hauteurTotale - centre, w),
                epaisseur: EPAISSEUR_FILET * taille,
            },
            ...place.dessins,
        ],
    };
}
/** Le glyphe des grands opérateurs. `integrale` est prête, l'analyseur ne la produit pas encore. */
const SYMBOLE_GRAND_OPERATEUR = {
    somme: '∑',
    produit: '∏',
    integrale: '∫',
};
function disposerGrandOperateur(noeud, ctx, dedans, script) {
    const taille = tailleDe(ctx.niveau);
    const symbole = SYMBOLE_GRAND_OPERATEUR[noeud.operateur] ?? '∑';
    const corps = disposerNoeud(noeud.corps, dedans);
    if (corps === null)
        return null;
    const inf = noeud.borneInf === undefined ? null : disposerNoeud(noeud.borneInf, script);
    const sup = noeud.borneSup === undefined ? null : disposerNoeud(noeud.borneSup, script);
    if ((noeud.borneInf !== undefined && inf === null) ||
        (noeud.borneSup !== undefined && sup === null)) {
        return null;
    }
    // Le symbole est agrandi puis RECENTRÉ sur l'axe mathématique : sans cela un
    // ∑ de 1,6 cadratin plongerait sous la ligne de base du texte voisin.
    const brut = glyphe(symbole, taille * 1.6, 'operateur');
    const axe = AXE * taille;
    // Centre visuel du glyphe agrandi, ramené sur l'axe mathématique.
    const symb = translater(brut, 0, -axe + (brut.montee - brut.descente) / 2);
    const largeurTete = Math.max(symb.largeur, inf?.largeur ?? 0, sup?.largeur ?? 0);
    const air = 0.1 * taille;
    const dessins = [...decaler(symb, (largeurTete - symb.largeur) / 2, 0)];
    let montee = symb.montee;
    let descente = symb.descente;
    if (sup !== null) {
        const y = -(symb.montee + air + sup.descente);
        dessins.push(...decaler(sup, (largeurTete - sup.largeur) / 2, y));
        montee = Math.max(montee, -y + sup.montee);
    }
    if (inf !== null) {
        const y = symb.descente + air + inf.montee;
        dessins.push(...decaler(inf, (largeurTete - inf.largeur) / 2, y));
        descente = Math.max(descente, y + inf.descente);
    }
    const tete = { largeur: largeurTete, montee, descente, dessins };
    return enRangee([tete, vide(0.14 * taille), corps]);
}
/**
 * Met en page une expression. `null` = trop profonde ou trop lourde : le
 * rendu retombe alors sur le texte, jamais sur rien.
 */
export function disposerMath(noeud) {
    const boite = disposerNoeud(noeud, { niveau: 0, profondeur: 0 });
    if (boite === null)
        return null;
    if (boite.dessins.length > DESSINS_MAX)
        return null;
    if (!Number.isFinite(boite.largeur) || !Number.isFinite(boite.montee))
        return null;
    return boite;
}
//# sourceMappingURL=disposer.js.map